import { Availability } from '../state/AppState';
import { Provider, Location, Insurance, AppointmentTime, State, Service, Reason } from './types';
import { fetchProviderImages } from './providerImageService';

/**
 * Data transformation service for converting availability data into existing data structures.
 * 
 * This service transforms the availability data from the API into the formats expected
 * by the existing screens and components, using the first availability as the primary
 * data source while extracting all unique values for options.
 */

/**
 * Transform availability data into providers list.
 * 
 * Extracts unique providers from availability data, using the first availability
 * as the primary provider and adding others as alternatives.
 * 
 * @param availabilities - Array of availability objects
 * @returns Array of Provider objects
 */
export function transformToProviders(availabilities: Availability[]): Provider[] {
  if (!availabilities || availabilities.length === 0) {
    return [];
  }

  const providerMap = new Map<string, Provider>();
  
  availabilities.forEach(availability => {
    const key = `${availability.firstName}-${availability.lastName}-${availability.providerCode}`;
    if (!providerMap.has(key)) {
      providerMap.set(key, {
        id: availability.providerCode,
        name: `${availability.firstName} ${availability.lastName}`,
        specialty: availability.providerType
      });
    }
  });

  return Array.from(providerMap.values());
}

/**
 * Transform availability data into locations list.
 * 
 * Extracts unique locations from availability data, combining location name,
 * region, and state information.
 * 
 * @param availabilities - Array of availability objects
 * @returns Array of Location objects
 */
export function transformToLocations(availabilities: Availability[]): Location[] {
  if (!availabilities || availabilities.length === 0) {
    return [];
  }

  const locationMap = new Map<string, Location>();
  
  availabilities.forEach(availability => {
    const locationKey = availability.locationCode;
    if (!locationMap.has(locationKey)) {
      let locationName = availability.locationName;
      
      // Add region/state info for better identification
      if (availability.region) {
        locationName = `${locationName} (${availability.region})`;
      }
      
      locationMap.set(locationKey, {
        id: availability.locationCode,
        name: locationName
      });
    }
  });

  return Array.from(locationMap.values());
}

/**
 * Transform availability data into insurance list.
 * 
 * Extracts unique insurance providers from the insuranceInNetwork field,
 * splitting semicolon-separated values.
 * 
 * @param availabilities - Array of availability objects
 * @returns Array of Insurance objects
 */
export function transformToInsurances(availabilities: Availability[]): Insurance[] {
  if (!availabilities || availabilities.length === 0) {
    return [];
  }

  const insuranceSet = new Set<string>();
  
  availabilities.forEach(availability => {
    if (availability.insuranceInNetwork) {
      const insurances = availability.insuranceInNetwork.split(';').map(ins => ins.trim());
      insurances.forEach(insurance => {
        if (insurance && insurance !== 'null') {
          insuranceSet.add(insurance);
        }
      });
    }
  });

  return Array.from(insuranceSet).map((insurance, index) => ({
    id: (index + 1).toString(),
    name: insurance
  }));
}

/**
 * Transform availability data into services list.
 * 
 * Extracts unique services from the providerServices field,
 * splitting semicolon-separated values.
 * 
 * @param availabilities - Array of availability objects
 * @returns Array of Service objects
 */
export function transformToServices(availabilities: Availability[]): Service[] {
  if (!availabilities || availabilities.length === 0) {
    return [];
  }

  const serviceSet = new Set<string>();
  
  availabilities.forEach(availability => {
    if (availability.providerServices) {
      const specialties = availability.providerServices.split(';').map(spec => spec.trim());
      specialties.forEach(specialty => {
        if (specialty) {
          serviceSet.add(specialty);
        }
      });
    }
  });

  return Array.from(serviceSet).map((service, index) => ({
    id: (index + 1).toString(),
    name: service
  }));
}

/**
 * Transform availability data into reasons list.
 * 
 * Extracts unique specialties from the specialties field,
 * splitting semicolon-separated values.
 * 
 * @param availabilities - Array of availability objects
 * @returns Array of Reason objects
 */
export function transformToReasons(availabilities: Availability[]): Reason[] {
  if (!availabilities || availabilities.length === 0) {
    return [];
  }

  const reasonSet = new Set<string>();
  
  availabilities.forEach(availability => {
    if (availability.specialties) {
      const specialties = availability.specialties.split(';').map(spec => spec.trim());
      specialties.forEach(specialty => {
        if (specialty) {
          reasonSet.add(specialty);
        }
      });
    }
  });

  return Array.from(reasonSet).map((reason, index) => ({
    id: (index + 1).toString(),
    name: reason
  }));
}

/**
 * Transform availability data into states list.
 * 
 * Extracts unique states from the state field.
 * 
 * @param availabilities - Array of availability objects
 * @returns Array of State objects
 */
export function transformToStates(availabilities: Availability[]): State[] {
  if (!availabilities || availabilities.length === 0) {
    return [];
  }

  const stateSet = new Set<string>();
  
  availabilities.forEach(availability => {
    if (availability.region) {
      stateSet.add(availability.region);
    }
  });

  return Array.from(stateSet).map((state, index) => ({
    id: state.toLowerCase().replace(/\s+/g, ''),
    name: state
  }));
}

/**
 * Transform availability data into appointment times.
 * 
 * Generates appointment time slots based on the availability data,
 * using startDate, startTime, dayOfWeek, and interval information.
 * 
 * @param availabilities - Array of availability objects
 * @returns Array of AppointmentTime objects
 */
export function transformToAppointmentTimes(availabilities: Availability[]): AppointmentTime[] {
  if (!availabilities || availabilities.length === 0) {
    return [];
  }

  const appointmentTimes: AppointmentTime[] = [];
  
  availabilities.forEach((availability, index) => {
    // Parse the start date
    const startDate = new Date(availability.startDateIoi);
    const dateString = startDate.toISOString().split('T')[0]; // YYYY-MM-DD format
    
    // Format the time for display
    const timeDisplay = availability.startTimeSource || availability.startTime;
    
    // Create time range (assuming 1-hour appointments)
    const timeRange = `${timeDisplay} - ${getEndTime(timeDisplay)}`;
    
    appointmentTimes.push({
      id: (index + 1).toString(),
      date: dateString,
      time: timeDisplay,
      timeRange: timeRange
    });
  });

  return appointmentTimes;
}

/**
 * Get the primary provider from availability data.
 * 
 * Returns the first provider from the availability data as the primary provider.
 * 
 * @param availabilities - Array of availability objects
 * @returns Primary provider information or null
 */
export function getPrimaryProvider(availabilities: Availability[]): Provider | null {
  if (!availabilities || availabilities.length === 0) {
    return null;
  }

  const first = availabilities[0];
  return {
    id: first.providerCode,
    name: `${first.firstName} ${first.lastName}`,
    specialty: first.providerType
  };
}


/**
 * Helper function to calculate end time for appointment slots.
 * 
 * @param startTime - Start time string (e.g., "9:30am", "1:30pm")
 * @returns End time string
 */
function getEndTime(startTime: string): string {
  try {
    // Parse time and add 1 hour
    const time = startTime.toLowerCase().replace(/\s+/g, '');
    const timeMatch = time.match(/(\d{1,2}):?(\d{2})?\s*(am|pm)/);
    
    if (!timeMatch) return '10:30am';
    
    const hour = parseInt(timeMatch[1]);
    const minute = timeMatch[2] ? parseInt(timeMatch[2]) : 0;
    const period = timeMatch[3];
    
    // Convert to 24-hour format
    let hour24 = period === 'pm' && hour !== 12 ? hour + 12 : (period === 'am' && hour === 12 ? 0 : hour);
    
    // Add 1 hour
    hour24 = (hour24 + 1) % 24;
    
    // Convert back to 12-hour format
    const displayHour = hour24 === 0 ? 12 : (hour24 > 12 ? hour24 - 12 : hour24);
    const displayPeriod = hour24 < 12 ? 'am' : 'pm';
    
    return `${displayHour}:${minute.toString().padStart(2, '0')}${displayPeriod}`;
  } catch (error) {
    return '10:30am';
  }
}

/**
 * Get distinct regions from availability data.
 * 
 * Extracts unique regions from the region field.
 * 
 * @param availabilities - Array of availability objects
 * @returns Array of region objects with id and name
 */
export function getDistinctRegions(availabilities: Availability[]): Array<{id: string, name: string}> {
  if (!availabilities || availabilities.length === 0) {
    return [];
  }
  const regionMap = new Map<string, {id: string, name: string}>();
  
  availabilities.forEach((availability, index) => {
    if (availability.region && availability.region.trim() !== '') {
      const regionKey = availability.region.toLowerCase().replace(/\s+/g, '');
      if (!regionMap.has(regionKey)) {
        regionMap.set(regionKey, {
          id: regionKey,
          name: availability.region
        });
      }
    } else {
    }
  });

  const result = Array.from(regionMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  return result;
}

/**
 * Get specialties from availability data filtered by region.
 * 
 * Extracts unique specialties from the specialties field for a specific region.
 * 
 * @param availabilities - Array of availability objects
 * @param region - Region to filter by
 * @returns Array of specialty strings
 */
export function getSpecialtiesByRegion(availabilities: Availability[], region: string): string[] {
  if (!availabilities || availabilities.length === 0) {
    return [];
  }

  const specialtySet = new Set<string>();
  
  availabilities
    .filter(availability => 
      !region || availability.region === region
    )
    .forEach(availability => {
      if (availability.specialties) {
        const specialties = availability.specialties.split(';').map(specialty => specialty.trim());
        specialties.forEach(specialty => {
          if (specialty) {
            specialtySet.add(specialty);
          }
        });
      }
    });

  // Sort specialties alphabetically
  return Array.from(specialtySet).sort((a, b) => a.localeCompare(b));
}

/**
 * Get insurances from availability data filtered by region.
 * 
 * Extracts unique insurances from the insuranceInNetwork field for a specific region.
 * 
 * @param availabilities - Array of availability objects
 * @param region - Region to filter by
 * @returns Array of insurance strings
 */
export function getInsurancesByRegion(availabilities: Availability[], region: string): string[] {
  if (!availabilities || availabilities.length === 0) {
    return [];
  }

  const insuranceSet = new Set<string>();
  
  availabilities
    .filter(availability => 
      !region || availability.region === region
    )
    .forEach(availability => {
      if (availability.insuranceInNetwork) {
        const insurances = availability.insuranceInNetwork.split(';').map(insurance => insurance.trim());
        insurances.forEach(insurance => {
          if (insurance) {
            insuranceSet.add(insurance);
          }
        });
      }
    });

  return Array.from(insuranceSet);
}

/**
 * Get age groups from availability data filtered by region.
 * 
 * Extracts unique age groups from the ageRangeGroups field for a specific region.
 * 
 * @param availabilities - Array of availability objects
 * @param region - Region to filter by
 * @returns Array of age group objects with id, label, and range
 */
export function getAgeGroupsByRegion(availabilities: Availability[], region: string): Array<{id: string, label: string, range: string}> {
  if (!availabilities || availabilities.length === 0) {
    return [];
  }

  const ageGroupSet = new Set<string>();
  
  availabilities
    .filter(availability => 
      !region || region === '' || availability.region === region
    )
    .forEach(availability => {
      if (availability.ageRangeGroups) {
        const ageGroups = availability.ageRangeGroups.split(';').map(group => group.trim());
        ageGroups.forEach(group => {
          if (group) {
            ageGroupSet.add(group);
          }
        });
      }
    });

  // Convert to age group objects and sort by numerical lower range
  const ageGroupObjects = Array.from(ageGroupSet).map((ageGroup) => {
    // Extract range from age group string like "School-Aged (6-12)"
    const rangeMatch = ageGroup.match(/\(([^)]+)\)/);
    const range = rangeMatch ? rangeMatch[1] : '';
    
    // Create a clean label without the range
    const label = ageGroup.replace(/\s*\([^)]+\)/, '');
    
    return {
      id: ageGroup, // Use the full age group string as the ID
      label: label,
      range: range
    };
  });

  // Sort by numerical lower range
  return ageGroupObjects.sort((a, b) => {
    // Extract the first number from each range
    const getLowerAge = (range: string): number => {
      const match = range.match(/^(\d+)/);
      return match ? parseInt(match[1], 10) : 0;
    };
    
    const ageA = getLowerAge(a.range);
    const ageB = getLowerAge(b.range);
    
    return ageA - ageB;
  });
}

/**
 * Fetch provider images for all unique provider codes in availability data.
 * 
 * @param availabilities - Array of availability objects
 * @returns Promise<Map<string, string>> - Map of provider code to image URL
 */
export async function fetchProviderImagesForAvailability(availabilities: Availability[]): Promise<Map<string, string>> {
  if (!availabilities || availabilities.length === 0) {
    return new Map();
  }

  // Get unique provider codes
  const uniqueProviderCodes = Array.from(new Set(availabilities.map(a => a.providerCode)));

  // Fetch all provider images in parallel
  return await fetchProviderImages(uniqueProviderCodes);
}

/**
 * Gets the provider name from availability data, combining firstName and lastName.
 * Falls back to preferredProvider from state if no availability data.
 */
export function getProviderNameFromAvailability(availabilities: Availability[] | null, fallbackName: string | null = null): string {
  if (availabilities && availabilities.length > 0) {
    const firstAvailability = availabilities[0];
    return `${firstAvailability.firstName} ${firstAvailability.lastName}`;
  }
  
  return fallbackName || 'Provider';
}
